import styled, { css } from 'styled-components';
import StyleGuide from 'src/style-guide';
import constants from 'src/constants';

const { SIDEBAR_WIDTH } = constants.sidebar;
const THUMB_SLIDER_HEIGHT = 56; // in px

export const TopButtonsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.lightGrayFour}`};
`;

export const TopButton = styled.button`
  position: relative;
  height: 74px;
  width: 74px;
  margin: 14px 0 10.5px;
  border-radius: 4px;
  padding: 25px 0px;
  cursor: pointer;
  background-color: transparent;
  color: transparent;

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: transparent;
    color: transparent;

    &:hover {
      background-color: transparent;
      color: transparent;
    }
  }

  &:hover {
    background-color: transparent;
    color: transparent;
    path,
    rect {
      fill: ${({ theme }) => theme.colors.defaultActionDarkBlue};
    }
  }

  .hv-svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;

    ${({ disabled, theme }) =>
      disabled
        ? css`
            path,
            rect {
              fill: ${theme.colors.lightGrayFour};
            }
          `
        : ''}
  }
`;

export const SplitSidebarContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const DeleteButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

// Header
export const HeaderContainer = styled.div`
  display: flex;
  padding: 16px 10px;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.2);
  z-index: 1;
`;
export const SplitFacesText = styled.div`
  width: 100px;
  height: 19px;
  line-height: normal;
  letter-spacing: 0.5px;
  text-align: center;
  color: ${({ theme }) => theme.colors.trueBlack};
  ${({ theme }) => theme.mixins.font('bold', 16)}
`;

export const NumberOfSplitFacesText = styled.div`
  width: 50px;
  height: 19px;
  margin-left: 110px;
  line-height: normal;
  letter-spacing: 0.5px;
  text-align: right;
  color: ${({ theme }) => theme.colors.grayButtonNormalText};
  ${({ theme }) => theme.mixins.font('book', 16)}
`;

// Control Buttons
export const SliderControlButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SliderDirectionalButton = css`
  transition: 100ms;
  border-radius: 3px;
  width: 44px;
  height: 44px;
  background-color: ${({ theme }) => theme.colors.lightGrayText};
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.mediumGrayText};
  }
`;

export const SliderUpButton = styled.div`
  ${SliderDirectionalButton}
`;

export const SliderDownButton = styled.div`
  ${SliderDirectionalButton}
  margin-left: 5px;
`;

export const SliderUpIcon = styled.div`
  margin-left: 16px;
  display: inline-block;
  transform: translateY(-50%);
  object-fit: contain;
  transform: rotate(-90deg);

  .hv-svg svg {
    height: ${({ size }) => size}px;
    width: ${({ size }) => size}px;

    path {
      fill: ${({ theme }) => theme.colors.white};
      &:hover {
        fill: ${({ theme }) => theme.colors.white};
      }
    }
  }
`;

export const SliderDownIcon = styled.div`
  margin-right: 16px;
  margin-top: 18px;
  display: inline-block;
  transform: translateY(-50%);
  object-fit: contain;
  transform: rotate(90deg);

  .hv-svg svg {
    height: ${({ size }) => size}px;
    width: ${({ size }) => size}px;

    path {
      fill: ${({ theme }) => theme.colors.white};
      &:hover {
        fill: ${({ theme }) => theme.colors.white};
      }
    }
  }
`;

export const SliderControlBarTextBox = styled.div`
  width: 156px;
  height: 44px;
  margin-left: 5px;
  padding: 11px;
  border-radius: 2px;
  background-color: ${({ theme }) => theme.colors.mediumGrayText};
  line-height: normal;
  letter-spacing: 0.7px;
  text-align: center;
  color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => theme.mixins.font('book', 18)}
`;

// Edit Panel
export const EditButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
`;

export const CancelButton = styled.div`
  width: 120px;
  height: 44px;
  padding: 14px;
  line-height: normal;
  letter-spacing: 0.7px;
  text-align: center;
  color: ${({ theme }) => theme.colors.trueBlack};
  ${({ theme }) => theme.mixins.font('medium', 16)}
  border-radius: 2.5px;
  border: solid 1px ${({ theme }) => theme.colors.darkGray3};
  background-color: ${({ theme }) => theme.colors.lightGrayTwo};
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.grayButtonHoverBackground};
  }
`;

export const ConfirmButton = styled.div`
  width: 130px;
  height: 44px;
  margin-left: 8px;
  padding: 14px;
  line-height: normal;
  letter-spacing: 0.7px;
  text-align: center;
  color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => theme.mixins.font('medium', 16)}
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.defaultActionBlue};
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.defaultActionDarkBlue};
  }
`;

// SliderBar
export const OffsetLabel = styled.div`
  letter-spacing: 0.7px;
  text-align: center;
  padding: 24px;
  color: ${({ theme }) => theme.colors.lightGrayTextTwo};
  ${({ theme }) => theme.mixins.font('book', 18)}
`;

export const SliderControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  position: relative;
`;

export const SliderBar = styled.div`
  position: absolute;
  width: 8px;
  height: ${`calc(100% - ${THUMB_SLIDER_HEIGHT}px)`};
  border-radius: 17px;
  background-color: ${({ theme }) =>
    theme.colors.lightGraySplitSliderBackground};
  box-shadow: inset 1px 2px 3px 0
    ${({ theme }) => theme.colors.graySplitSliderShadow};
`;

export const SliderBarFill = styled.div`
  position: absolute;
  height: ${({ fillPercentage }) => `${fillPercentage * 100}%`};
  bottom: 0px;
  width: 8px;
  border-radius: 17px;
  background-color: ${({ theme }) => theme.colors.trueBlack};
  box-shadow: inset 0 1px 3px 0
    ${({ theme }) => theme.colors.graySplitSliderShadow};
`;

export const SliderBarTicks = styled.div`
  position: absolute;
  top: ${({ percentHeight }) => `${percentHeight * 100}%`};
  width: 8px;
  height: 3px;
  background-color: ${({ theme }) =>
    theme.colors.lightGraySplitSliderBackground};
  box-shadow: inset 1px 2px 3px 0
    ${({ theme }) => theme.colors.graySplitSliderTicks};
`;

// Control Slider Panel
export const InputSlider = styled.input`
  user-select: none;
  position: absolute;
  z-index: 2;
  cursor: pointer;
  opacity: 0; /* hide the default input range element */
  -webkit-appearance: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  /* IMPORTANT!: height and width are flipped because of the -90 degree rotation */
  transform: rotate(-90deg);
  width: ${({ parentHeight }) => parentHeight}px;
  height: ${SIDEBAR_WIDTH}px;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;

    /* IMPORTANT!: height and width are flipped because of the -90 degree rotation */
    height: ${SIDEBAR_WIDTH}px;
    width: ${THUMB_SLIDER_HEIGHT}px;
  }

  &::-ms-tooltip {
    visibility: hidden;
  }
`;

export const ButtonCircle = styled.img`
  height: 90%;
`;

export const SliderThumbContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 0;
  bottom: ${({ fillPercentage }) =>
    `calc((100% - ${THUMB_SLIDER_HEIGHT}px) * ${fillPercentage})`};
  height: ${THUMB_SLIDER_HEIGHT}px;
  width: 100%;

  &:hover {
    cursor: pointer;
  }
`;

export const SliderBarCurrentOffsetText = styled.div`
  position: absolute;
  right: 16px;
  top: 8px;
  line-height: normal;
  letter-spacing: 0.8px;
  text-align: right;
  color: ${({ theme }) => theme.colors.darkRed};
  ${({ theme }) => theme.mixins.font('book', 18)}
`;

export const SliderButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  cursor: pointer;
  z-index: 1;
`;

export const SliderLineContainer = styled.div`
  position: absolute;
  width: 234px;
  object-fit: contain;
`;

export const SplitSliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 2;
`;

// delete split
export const DeleteSplitContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  padding: 0 32px 24px 32px;
`;

export const TrashcanIconAndHeaderContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const TrashcanIconAndHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const BigTrashcanIconContainer = styled(StyleGuide.Icon)`
  margin-bottom: 16px;

  svg {
    height: ${({ size }) => size}px;
    width: ${({ size }) => size}px;

    use {
      fill: ${({ theme }) => theme.colors.lightGrayFour};
    }
  }
`;

export const DeleteSplitHeader = styled.div`
  position: relative;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.2;
  letter-spacing: normal;
  color: ${({ theme }) => theme.colors.lightGrayFour};
  ${({ theme }) => theme.mixins.font('medium', 30)}
`;

export const SplitRemovedMessageText = styled.div`
  width: 100%;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.38;
  letter-spacing: normal;
  text-align: center;
  color: ${({ theme }) => theme.colors.mediumGrayText};
  ${({ theme }) => theme.mixins.font('book', 13)}
`;
